import { get } from 'react-hook-form';

export const getMapBy = <
  D extends Record<PropertyKey, any>,
  K extends keyof D | string,
  Transformed = D,
>(
  data: D[],
  key: K | ((datum: D) => any),
  transform?: (datum: D) => Transformed,
) => {
  const isDeepKey = typeof key === 'string' && key.includes('.');
  const isKeyFn = typeof key === 'function';

  return new Map<K extends keyof D ? D[K] : any, Transformed>(
    data.reduce<[D[K], Transformed][]>(
      (acc, datum) => [
        ...acc,
        [
          isDeepKey
            ? get(datum, key as string)
            : isKeyFn
              ? key(datum)
              : datum[key],
          transform ? transform(datum) : datum,
        ],
      ],
      [],
    ),
  );
};

export const getFromMapIfExists = <K, V>(
  map: Map<K, V>,
  key: K,
  typeGuard?: (value: V) => boolean,
): V | undefined => {
  const value = map.get(key);

  if (value === undefined) {
    return undefined;
  }

  if (typeGuard && !typeGuard(value)) {
    throw new Error(`Value for key "${String(key)}" has an unexpected type`);
  }

  return value;
};

export const getFromMapSafely = <
  K,
  V extends Record<PropertyKey, any>,
  T extends V,
>(
  map: Map<K, V>,
  key: K,
  typeGuard?: (value: V) => value is T,
): typeof typeGuard extends undefined ? V : T => {
  const value = map.get(key);

  if (value === undefined) {
    throw new Error(`Key "${String(key)}" not found in map`);
  }

  if (typeGuard && !typeGuard(value)) {
    throw new Error(`Value for key "${String(key)}" has an unexpected type`);
  }

  return value;
};
